import type {
  InvestorMetricWindow,
  InvestorPerformanceQuery
} from '@/lib/investor-analytics';

/**
 * The investor's own reads (`/api/v1/investor/:address/...`), keyed by
 * wallet and chain. Shared, not Portfolio's: a deposit or withdrawal on a
 * vault page invalidates this whole tree (`portfolioKeys.all`), and the
 * vault page must not import the Portfolio feature (and its views) to do it.
 *
 * The query shapes are structural so this module needs nothing from a
 * feature; the Portfolio hooks pass their own validated queries.
 */
interface HoldingsKeyQuery {
  page?: number;
  limit?: number;
  kind?: string;
  sort?: string;
}

interface PnlKeyQuery {
  window?: InvestorMetricWindow;
  granularity?: string;
}

function scopeAddress(address: string | undefined) {
  return address?.toLowerCase();
}

export const portfolioKeys = {
  all: ['portfolio'] as const,
  hygiene: (address: string | undefined, chainId: number) =>
    [...portfolioKeys.all, 'hygiene', scopeAddress(address), chainId] as const,
  holdings: (
    address: string | undefined,
    chainId: number,
    params: HoldingsKeyQuery
  ) =>
    [
      ...portfolioKeys.all,
      'holdings',
      scopeAddress(address),
      chainId,
      params.page ?? 1,
      // Mirrors use-investor-holdings' defaultHoldingsQuery so an explicit
      // and a defaulted 100-row request share one cache entry.
      params.limit ?? 100,
      params.kind ?? 'all',
      params.sort ?? 'value'
    ] as const,
  riskMetrics: (
    address: string | undefined,
    chainId: number,
    window: InvestorMetricWindow | undefined
  ) =>
    [
      ...portfolioKeys.all,
      'riskMetrics',
      scopeAddress(address),
      chainId,
      window ?? 'default'
    ] as const,
  performance: (
    address: string | undefined,
    chainId: number,
    params: InvestorPerformanceQuery
  ) =>
    [
      ...portfolioKeys.all,
      'performance',
      scopeAddress(address),
      chainId,
      params.from ?? null,
      params.to ?? null,
      params.interval ?? null,
      // An empty selection is the same request as no selection.
      params.benchmarks?.join(',') || null
    ] as const,
  transactions: (
    address: string | undefined,
    chainId: number,
    page: number,
    limit: number
  ) =>
    [
      ...portfolioKeys.all,
      'transactions',
      scopeAddress(address),
      chainId,
      page,
      limit
    ] as const,
  pnl: (address: string | undefined, chainId: number, params: PnlKeyQuery) =>
    [
      ...portfolioKeys.all,
      'pnl',
      scopeAddress(address),
      chainId,
      params.window ?? 'default',
      params.granularity ?? 'window'
    ] as const,
  fees: (
    address: string | undefined,
    chainId: number,
    window: InvestorMetricWindow | undefined
  ) =>
    [
      ...portfolioKeys.all,
      'fees',
      scopeAddress(address),
      chainId,
      window ?? 'default'
    ] as const,
  attribution: (
    address: string | undefined,
    chainId: number,
    window: InvestorMetricWindow | undefined
  ) =>
    [
      ...portfolioKeys.all,
      'attribution',
      scopeAddress(address),
      chainId,
      window ?? 'default'
    ] as const
};
